import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  FileText,
  Clock,
  Users,
  Calendar,
  Save,
  GripVertical,
  ToggleLeft,
  ToggleRight,
  Loader2,
  CheckCircle2,
  Plus,
  Trash2,
  Edit,
  XCircle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';

import {
  useRejectionTemplates,
  useCreateRejectionTemplate,
  useUpdateRejectionTemplate,
  useDeleteRejectionTemplate,
} from '../../hooks/useKycDepositValidation';

interface DocumentTypeConfig {
  id: string;
  documentTypeId: string;
  documentTypeName: string;
  documentTypeCode: string;
  isRequired: boolean;
  displayOrder: number;
  customInstructions?: string;
  isActive: boolean;
}

interface SlaConfig {
  id: string;
  firstAnalysisHours: number;
  resubmissionHours: number;
  additionalDocsHours: number;
  alertPercentage: number;
  isActive: boolean;
}

interface TwoLevelConfig {
  id: string;
  enabledForPJ: boolean;
  enabledForPF: boolean;
  afterRejections: number;
  transactionThreshold: number;
  isActive: boolean;
}

interface DocumentValidityConfig {
  id: string;
  documentTypeId: string;
  documentTypeName: string;
  validityDays: number;
  alertDaysBefore: number;
  isActive: boolean;
}

type TabType = 'documents' | 'sla' | 'approval' | 'validity' | 'rejection-templates';

export function KycSettings() {
  const [activeTab, setActiveTab] = useState<TabType>('documents');
  const queryClient = useQueryClient();

  const tabs = [
    { id: 'documents' as TabType, label: 'Tipos de Documentos', icon: FileText },
    { id: 'sla' as TabType, label: 'SLA', icon: Clock },
    { id: 'approval' as TabType, label: 'Aprovação em Dois Níveis', icon: Users },
    { id: 'validity' as TabType, label: 'Validade de Documentos', icon: Calendar },
    { id: 'rejection-templates' as TabType, label: 'Templates de Rejeição', icon: FileText },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-3">
          <Settings className="w-7 h-7 text-primary-400" />
          Configurações de KYC
        </h1>
        <p className="text-surface-400 mt-1">
          Configure tipos de documentos, SLA, aprovação em dois níveis e validade
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-700 pb-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-colors",
                activeTab === tab.id
                  ? "bg-surface-800 text-primary-400 border-b-2 border-primary-400"
                  : "text-surface-400 hover:text-surface-100"
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="glass rounded-xl p-6">
        {activeTab === 'documents' && <DocumentTypesTab />}
        {activeTab === 'sla' && <SlaTab />}
        {activeTab === 'approval' && <TwoLevelApprovalTab />}
        {activeTab === 'validity' && <DocumentValidityTab />}
        {activeTab === 'rejection-templates' && <RejectionTemplatesTab />}
      </div>
    </div>
  );
}

// ============================================
// Document Types Tab
// ============================================
function DocumentTypesTab() {
  const queryClient = useQueryClient();

  const { data: configs, isLoading } = useQuery<DocumentTypeConfig[]>({
    queryKey: ['kyc-document-type-configs'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/settings/document-types');
      return response.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { configs: Partial<DocumentTypeConfig & { documentTypeId?: string }>[] }) => {
      const response = await api.post('/admin/kyc/settings/document-types', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-document-type-configs'] });
    },
  });

  const toggleRequired = (id: string, currentValue: boolean) => {
    const config = configs?.find(c => c.id === id || c.documentTypeId === id);
    if (config) {
      updateMutation.mutate({
        configs: [{
          documentTypeId: config.documentTypeId,
          isRequired: !currentValue,
          isActive: config.isActive,
        }],
      });
    }
  };

  const toggleActive = (id: string, currentValue: boolean) => {
    const config = configs?.find(c => c.id === id || c.documentTypeId === id);
    if (config) {
      updateMutation.mutate({
        configs: [{
          documentTypeId: config.documentTypeId,
          isActive: !currentValue,
          isRequired: config.isRequired,
        }],
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-surface-100">Tipos de Documentos</h2>
        <p className="text-sm text-surface-400">
          Configure quais documentos são obrigatórios para verificação KYC
        </p>
      </div>

      <div className="space-y-2">
        {configs?.map((config, index) => (
          <div
            key={config.id}
            className={cn(
              "flex items-center gap-4 p-4 rounded-lg border transition-colors",
              config.isActive
                ? "bg-surface-800 border-surface-700"
                : "bg-surface-900 border-surface-800 opacity-60"
            )}
          >
            <GripVertical className="w-5 h-5 text-surface-500 cursor-grab" />
            
            <div className="flex-1">
              <h3 className="font-medium text-surface-100">{config.documentTypeName}</h3>
              <p className="text-sm text-surface-500">{config.documentTypeCode}</p>
              {config.customInstructions && (
                <p className="text-xs text-surface-400 mt-1">{config.customInstructions}</p>
              )}
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => toggleRequired(config.id, config.isRequired)}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                  config.isRequired
                    ? "bg-red-500/20 text-red-400"
                    : "bg-surface-700 text-surface-400"
                )}
              >
                {config.isRequired ? 'Obrigatório' : 'Opcional'}
              </button>

              <button
                onClick={() => toggleActive(config.id, config.isActive)}
                className="text-surface-400 hover:text-surface-100 transition-colors"
              >
                {config.isActive ? (
                  <ToggleRight className="w-8 h-8 text-green-400" />
                ) : (
                  <ToggleLeft className="w-8 h-8" />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {updateMutation.isPending && (
        <div className="flex items-center gap-2 text-sm text-primary-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Salvando...
        </div>
      )}
    </div>
  );
}

// ============================================
// SLA Tab
// ============================================
function SlaTab() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<SlaConfig>>({});
  const [initialized, setInitialized] = useState(false);

  const { data: config, isLoading } = useQuery<SlaConfig>({
    queryKey: ['kyc-sla-config'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/settings/sla');
      return response.data;
    },
  });

  // Update form data when config loads
  if (config && !initialized) {
    setFormData(config);
    setInitialized(true);
  }

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<SlaConfig>) => {
      const response = await api.put('/admin/kyc/settings/sla', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-sla-config'] });
    },
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-surface-100">Configuração de SLA</h2>
          <p className="text-sm text-surface-400">
            Defina os prazos para análise de documentos KYC
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={updateMutation.isPending}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
        >
          {updateMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Salvar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Horas para Primeira Análise
          </label>
          <input
            type="number"
            value={formData.firstAnalysisHours || ''}
            onChange={(e) => setFormData({ ...formData, firstAnalysisHours: Number(e.target.value) })}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
            placeholder="48"
          />
          <p className="text-xs text-surface-500 mt-1">
            Prazo máximo para iniciar análise de nova submissão
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Horas para Resubmissão
          </label>
          <input
            type="number"
            value={formData.resubmissionHours || ''}
            onChange={(e) => setFormData({ ...formData, resubmissionHours: Number(e.target.value) })}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
            placeholder="24"
          />
          <p className="text-xs text-surface-500 mt-1">
            Prazo máximo para analisar documentos reenviados
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Horas para Docs Adicionais
          </label>
          <input
            type="number"
            value={formData.additionalDocsHours || ''}
            onChange={(e) => setFormData({ ...formData, additionalDocsHours: Number(e.target.value) })}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
            placeholder="24"
          />
          <p className="text-xs text-surface-500 mt-1">
            Prazo máximo para analisar documentos adicionais
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Percentual de Alerta (%)
          </label>
          <input
            type="number"
            value={formData.alertPercentage || ''}
            onChange={(e) => setFormData({ ...formData, alertPercentage: Number(e.target.value) })}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
            placeholder="80"
            min="0"
            max="100"
          />
          <p className="text-xs text-surface-500 mt-1">
            Alerta quando atingir este percentual do prazo
          </p>
        </div>
      </div>

      {updateMutation.isSuccess && (
        <div className="flex items-center gap-2 text-sm text-green-400">
          <CheckCircle2 className="w-4 h-4" />
          Configurações salvas com sucesso
        </div>
      )}
    </div>
  );
}

// ============================================
// Two Level Approval Tab
// ============================================
function TwoLevelApprovalTab() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<TwoLevelConfig>>({});
  const [initialized, setInitialized] = useState(false);

  const { data: config, isLoading } = useQuery<TwoLevelConfig>({
    queryKey: ['kyc-two-level-config'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/settings/two-level-approval');
      return response.data;
    },
  });

  // Update form data when config loads
  if (config && !initialized) {
    setFormData(config);
    setInitialized(true);
  }

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<TwoLevelConfig>) => {
      const response = await api.put('/admin/kyc/settings/two-level-approval', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-two-level-config'] });
    },
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-surface-100">Aprovação em Dois Níveis</h2>
          <p className="text-sm text-surface-400">
            Configure quando exigir aprovação de supervisor
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={updateMutation.isPending}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
        >
          {updateMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Salvar
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-surface-800 rounded-lg">
          <div>
            <h3 className="font-medium text-surface-100">Habilitado para Pessoa Jurídica</h3>
            <p className="text-sm text-surface-400">
              Exigir aprovação de supervisor para KYC de empresas
            </p>
          </div>
          <button
            onClick={() => setFormData({ ...formData, enabledForPJ: !formData.enabledForPJ })}
            className="text-surface-400 hover:text-surface-100 transition-colors"
          >
            {formData.enabledForPJ ? (
              <ToggleRight className="w-10 h-10 text-green-400" />
            ) : (
              <ToggleLeft className="w-10 h-10" />
            )}
          </button>
        </div>

        <div className="flex items-center justify-between p-4 bg-surface-800 rounded-lg">
          <div>
            <h3 className="font-medium text-surface-100">Habilitado para Pessoa Física</h3>
            <p className="text-sm text-surface-400">
              Exigir aprovação de supervisor para KYC de pessoas físicas
            </p>
          </div>
          <button
            onClick={() => setFormData({ ...formData, enabledForPF: !formData.enabledForPF })}
            className="text-surface-400 hover:text-surface-100 transition-colors"
          >
            {formData.enabledForPF ? (
              <ToggleRight className="w-10 h-10 text-green-400" />
            ) : (
              <ToggleLeft className="w-10 h-10" />
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Após X Rejeições
          </label>
          <input
            type="number"
            value={formData.afterRejections || ''}
            onChange={(e) => setFormData({ ...formData, afterRejections: Number(e.target.value) })}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
            placeholder="2"
            min="0"
          />
          <p className="text-xs text-surface-500 mt-1">
            Exigir supervisor após este número de rejeições
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Threshold de Transação (R$)
          </label>
          <input
            type="number"
            value={formData.transactionThreshold || ''}
            onChange={(e) => setFormData({ ...formData, transactionThreshold: Number(e.target.value) })}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
            placeholder="50000"
            min="0"
          />
          <p className="text-xs text-surface-500 mt-1">
            Exigir supervisor para contas com transações acima deste valor
          </p>
        </div>
      </div>

      {updateMutation.isSuccess && (
        <div className="flex items-center gap-2 text-sm text-green-400">
          <CheckCircle2 className="w-4 h-4" />
          Configurações salvas com sucesso
        </div>
      )}
    </div>
  );
}

// ============================================
// Document Validity Tab
// ============================================
function DocumentValidityTab() {
  const queryClient = useQueryClient();

  const { data: configs, isLoading } = useQuery<DocumentValidityConfig[]>({
    queryKey: ['kyc-document-validity-configs'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/settings/document-validity');
      return response.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string; validityDays: number; alertDaysBefore: number }) => {
      const response = await api.put(`/admin/kyc/settings/document-validity/${data.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-document-validity-configs'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-surface-100">Validade de Documentos</h2>
          <p className="text-sm text-surface-400">
            Configure a validade e alertas de expiração por tipo de documento
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-surface-700">
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Tipo de Documento</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Validade (dias)</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Alerta (dias antes)</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Status</th>
            </tr>
          </thead>
          <tbody>
            {configs?.map((config) => (
              <tr key={config.id} className="border-b border-surface-800">
                <td className="py-3 px-4 text-surface-100">{config.documentTypeName}</td>
                <td className="py-3 px-4">
                  <input
                    type="number"
                    defaultValue={config.validityDays}
                    onBlur={(e) => {
                      if (Number(e.target.value) !== config.validityDays) {
                        updateMutation.mutate({
                          id: config.id,
                          validityDays: Number(e.target.value),
                          alertDaysBefore: config.alertDaysBefore,
                        });
                      }
                    }}
                    className="w-24 px-3 py-1 bg-surface-800 border border-surface-700 rounded text-surface-100 focus:outline-none focus:border-primary-500"
                  />
                </td>
                <td className="py-3 px-4">
                  <input
                    type="number"
                    defaultValue={config.alertDaysBefore}
                    onBlur={(e) => {
                      if (Number(e.target.value) !== config.alertDaysBefore) {
                        updateMutation.mutate({
                          id: config.id,
                          validityDays: config.validityDays,
                          alertDaysBefore: Number(e.target.value),
                        });
                      }
                    }}
                    className="w-24 px-3 py-1 bg-surface-800 border border-surface-700 rounded text-surface-100 focus:outline-none focus:border-primary-500"
                  />
                </td>
                <td className="py-3 px-4">
                  {config.isActive ? (
                    <span className="text-green-400 text-sm">Ativo</span>
                  ) : (
                    <span className="text-surface-500 text-sm">Inativo</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {updateMutation.isPending && (
        <div className="flex items-center gap-2 text-sm text-primary-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Salvando...
        </div>
      )}
    </div>
  );
}

// ============================================
// Rejection Templates Tab
// ============================================
interface RejectionTemplate {
  id: string;
  title: string;
  reason: string;
  isActive: boolean;
  createdAt: string;
}

function RejectionTemplatesTab() {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formReason, setFormReason] = useState('');

  const { data: templates, isLoading } = useRejectionTemplates();
  const createMutation = useCreateRejectionTemplate();
  const updateMutation = useUpdateRejectionTemplate();
  const deleteMutation = useDeleteRejectionTemplate();

  const handleCreate = () => {
    if (!formTitle.trim() || !formReason.trim()) return;
    createMutation.mutate(
      { title: formTitle, reason: formReason },
      {
        onSuccess: () => {
          setFormTitle('');
          setFormReason('');
          setShowForm(false);
        },
      },
    );
  };

  const handleUpdate = () => {
    if (!editingId || !formTitle.trim() || !formReason.trim()) return;
    updateMutation.mutate(
      { id: editingId, title: formTitle, reason: formReason },
      {
        onSuccess: () => {
          setFormTitle('');
          setFormReason('');
          setEditingId(null);
          setShowForm(false);
        },
      },
    );
  };

  const startEdit = (template: RejectionTemplate) => {
    setEditingId(template.id);
    setFormTitle(template.title);
    setFormReason(template.reason);
    setShowForm(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-surface-100">Templates de Rejeição</h2>
          <p className="text-sm text-surface-400">
            Motivos pré-definidos para agilizar a rejeição de KYC
          </p>
        </div>
        <button
          onClick={() => {
            setEditingId(null);
            setFormTitle('');
            setFormReason('');
            setShowForm(!showForm);
          }}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Novo Template
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="p-4 bg-surface-800 rounded-lg border border-surface-700 space-y-3">
          <h3 className="font-medium text-surface-100">
            {editingId ? 'Editar Template' : 'Novo Template'}
          </h3>
          <div>
            <label className="block text-sm text-surface-400 mb-1">Título</label>
            <input
              type="text"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Ex: Documento ilegível"
              className="w-full px-4 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm text-surface-400 mb-1">Motivo da Rejeição</label>
            <textarea
              value={formReason}
              onChange={(e) => setFormReason(e.target.value)}
              placeholder="Ex: O documento enviado está ilegível. Por favor, envie uma foto nítida..."
              rows={3}
              className="w-full px-4 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500 resize-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }}
              className="px-4 py-2 text-surface-400 hover:text-surface-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={editingId ? handleUpdate : handleCreate}
              disabled={!formTitle.trim() || !formReason.trim() || createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              {editingId ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        </div>
      )}

      {/* Templates List */}
      {(templates as RejectionTemplate[])?.length > 0 ? (
        <div className="space-y-2">
          {(templates as RejectionTemplate[]).map((template) => (
            <div
              key={template.id}
              className="flex items-start gap-4 p-4 bg-surface-800 rounded-lg border border-surface-700"
            >
              <div className="flex-1">
                <h3 className="font-medium text-surface-100">{template.title}</h3>
                <p className="text-sm text-surface-400 mt-1">{template.reason}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => startEdit(template)}
                  className="p-2 text-surface-400 hover:text-primary-400 hover:bg-surface-700 rounded-lg transition-colors"
                  title="Editar"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteMutation.mutate(template.id)}
                  disabled={deleteMutation.isPending}
                  className="p-2 text-surface-400 hover:text-red-400 hover:bg-surface-700 rounded-lg transition-colors"
                  title="Excluir"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-surface-600 mx-auto mb-3" />
          <p className="text-surface-400">Nenhum template de rejeição cadastrado</p>
          <p className="text-sm text-surface-500 mt-1">
            Crie templates para agilizar o processo de rejeição
          </p>
        </div>
      )}
    </div>
  );
}

export default KycSettings;
